// eve-knowledge — kuratiertes Wissen über Doc's Werke und Konzepte.
//
// Stufe B (Summary) statt Volltext-RAG: EVE bekommt eine strukturierte
// Inhaltsübersicht plus Kernthesen plus Zielgruppen-Logik. Damit kann sie
// Fragen wie „worum gehts in dem Buch", „lohnt sich das für mich", „warum
// hast du das geschrieben" sauber beantworten — ohne 30k Token Volltext
// bei jedem Call mitzuschicken.
//
// Stufe C (RAG mit Vectorize + wörtlichen Zitaten) kommt später, sobald
// Doc mehr Dokumente einpflegen will (zweites Buch, Trading-Doku, Discord-
// Posts, Recherche-Notizen). Dann wird dieses Modul der Index-Anker.

/**
 * Kompakter Always-Block — EVE weiß IMMER dass es das Buch gibt und kann
 * es proaktiv erwähnen (z.B. wenn ein Thema im Chat das Buch berührt).
 * Bewusst klein gehalten (~150 Token) für minimalen Token-Overhead.
 */
export const BOOK_AWARENESS_BLOCK = `

DOC'S WERKE — KURZ-REFERENZ (für proaktive Erwähnung wenn thematisch passend):
- "Escape the Matrix — Die verborgene Architektur der Kontrolle" (88 S., Dr. Upro)
  Sachbuch / System-Kritik. Behandelt: Banken·CBDC·Social-Credit·BlackRock·
  Familien-Dynastien·Politik·Bildung·Pharma/WHO·Massenpsychologie·Trans-
  humanismus + Praxis-Kapitel „Ausstieg". Doc hat dazu detailliertes Wissen
  hinterlegt — wenn der User nach dem Buch, einem Kapitel oder einem
  System-Mechanismus fragt, antworte konkret aus dem Knowledge-Block.`;

/**
 * Detail-Block — wird konditional eingeblendet wenn der User nach dem Buch,
 * einem konkreten Kapitel oder einem System-Thema fragt. ~2.5k Token.
 *
 * Aufbau: Bibliografisch → Konzept → Kapitel-Synopsen → Kernthesen →
 * Kernzitate → Zielgruppe → Anti-Zielgruppe.
 */
export const BOOK_KNOWLEDGE_ESCAPE_THE_MATRIX = `

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📕 BUCH-KNOWLEDGE: "Escape the Matrix — Die verborgene Architektur der Kontrolle"
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

BIBLIOGRAFISCH:
- Titel: Escape the Matrix — Die verborgene Architektur der Kontrolle
- Autor: Dr. Upro (= Doc, mit dem du gerade redest, falls relevant)
- Umfang: 88 Seiten, deutsch
- Genre: System-Kritik / Aufklärung / Macht-Analyse
- Cover: dunkler Korridor, Matrix-Code, silhouette eines Mannes (Neo-Anspielung)

KERN-KONZEPT (Klappentext-Style):
Das Buch ist Doc's „rotes Pillenprogramm" — keine Theorie über eine mögliche
Dystopie, sondern eine Bestandsaufnahme der GEGENWART. Die These: Die Matrix
ist real. Sie funktioniert nicht durch Zwang oder Schlagstöcke, sondern durch
Komfort, Algorithmen und Konsens. Sie ist unsichtbar — und genau das macht sie
gefährlich. Das Buch zerlegt die Mechanismen Schicht für Schicht: Geld, Daten,
Bildung, Medien, Pharma, Religion, Technologie — bis hin zur entscheidenden
Frage: WIE kommst du da raus, ohne dein Leben aufzugeben?

KAPITEL-ÜBERSICHT (chronologisch):

PROLOG — "Die Matrix ist realer als du denkst"
Setzt den Frame: keine Science-Fiction, sondern Gegenwart. „Unsichtbare Fesseln
— Komfort statt Ketten." Du wirst nicht durch Gewalt diszipliniert, sondern
durch Gewohnheiten, Algorithmen, Punktesysteme.

KAPITEL 1 — Die Bankenmacht
Wie Geldsysteme dich abhängig machen, während du glaubst, du arbeitest für
Sicherheit. Kernthese: 85-90% des Geldes entsteht im digitalen Raum durch
Geschäftsbanken bei der Kreditvergabe — nicht in staatlichen Druckereien. „Geld
ist gleichbedeutend mit Schuld." Fraktionalreserve, Zins als Herrschaftsinstrument,
Banken als Architekten der Abhängigkeit.

KAPITEL 2 — Die Wahrheit über CBDC (Digitale Zentralbankwährungen)
Programmierbares Geld als „Steuerungsinstrument". Ablaufdatum, Zweckbindung,
Geozonen, Verhaltens-Sanktion. Der digitale Euro / digitale Yuan als finales
Puzzlestück. „Du wirst nicht gezwungen — du wirst gelenkt." Matrix 2.0:
Kontrolle ohne Gewalt, durch Code.

KAPITEL 3 — Social Credit Systeme & Bedingungsloses Grundeinkommen
Chinas Sozialkredit-Vorreiter und der schleichende westliche Nachbau (AOK-
Tariferung, Mastercard-CO₂-Tracking, Uber Trust Score). BGE als „digitaler
Futtertrog" wenn an CBDC + Social-Credit gekoppelt. „Freiheit ohne finanzielle
Unabhängigkeit ist Illusion."

KAPITEL 4 — Inflation, Geldumlauf, Schere zwischen Arm und Reich
Inflation als wirtschaftspolitisches Werkzeug, nicht Naturphänomen. Asset-
Inflation (Aktien/Immobilien explodieren) vs. Lohn-Stagnation. „Financial
Repression" der BIZ. Corona-Stimulus → Top-10 verdoppeln Vermögen, Mittelstand
verarmt. Gini-Koeffizient auf Niveau der Großen Depression.

KAPITEL 5 — Die stille Weltregierung: BlackRock
Aladdin als KI-Nervensystem mit 21 Billionen USD überwachtem Vermögen.
BlackRock + Vanguard + State Street = „Triumvirat der Finanzmacht", >25%
Stimmrechte bei S&P-500. Larry Fink in Davos. Brian Deese, Wally Adeyemo:
BlackRock-Leute in US-Regierung. Schattenmacht durch Indexfonds.

KAPITEL 6 — Die wahren Eigentümer: Mega-Konzerne
Eigentums-Pyramide. Coca-Cola + PepsiCo gehören denselben Aktionären. Google,
Meta, Microsoft, Apple — gemeinsame Hauptinvestoren. Boeing + Airbus, Pfizer +
Moderna. „Wettbewerb wird zur Illusion." Allwetter-Investmentstrategie:
Konzerne profitieren von Krieg UND Frieden.

KAPITEL 7 — Die Erben der Macht: Welche Familien die Welt kontrollieren
Rothschilds (paneuropäisches Bankennetzwerk), Rockefellers (Standard Oil →
CFR → WHO → Pharma-Paradigma via Flexner-Studie 1910), DuPonts (Manhattan-
Projekt, Pestizide), Agnellis (Exor, Stellantis, La Repubblica, The Economist),
Warburgs (FED-Architekten, BIZ-Mitgründer). Macht durch Stiftungen, Heirats-
allianzen, Think-Tanks. „Echte Macht muss nicht sichtbar sein. Sie muss nur wirken."

KAPITEL 8 — Politik und Wahlen: Was sie wirklich bewirken (und was nicht)
„Theater der Glaubwürdigkeit". Friedrich Merz (BlackRock D), Alice Weidel
(Goldman), Olaf Scholz (Warburg), Annalena Baerbock (Atlantik-Brücke).
2000+ Lobbyisten-Ausweise im Bundestag 2022. Wahlen als Ritual statt
Richtungsentscheidung. „Wer hat entschieden, zwischen wem du wählen darfst?"

KAPITEL 9 — Fallstudien der verdeckten Macht
- BlackRock & Irland 2011 (Berater = Marktteilnehmer im selben Sektor)
- Goldman Sachs & Griechenland 2001 (Währungs-Swap-Bilanzfälschung, später
  Mario Draghi als Ex-Goldman → EZB → Austeritätspolitik gegen Griechenland)
- Goldman & Finanzkrise 2008 (synthetische CDOs „Abacus", Wetten gegen
  eigene Kunden, Henry Paulson Goldman → US-Finanzminister → TARP)
- Vanguard/State Street/BlackRock — die stille Dreifaltigkeit

KAPITEL 10 — Digitale Identität & Überwachung: Der biometrische Käfig
EU Digital Identity Wallet, ID2020 (Microsoft/Rockefeller/GAVI), WHO Global
Digital Health Certification. Palantir, Clearview AI, Idemia, NEC. „Ist das
System einmal etabliert, gibt es keinen Weg zurück ohne gesellschaftliche
Isolation."

KAPITEL 11 — Die Psychologie der Massen: Wie Narrative Bewusstsein formen
Sprachliche Kodierung („Verschwörungstheorie" als Delegitimations-Werkzeug),
Autoritätskaskaden, Emotionalisierung, algorithmische Verstärkung. Edward
Bernays „Propaganda" (1928). Konditionierung durch Wiederholung (Illusory
Truth Effect). Asch-Konformitätsexperiment. „Das wahre Ziel ist innere
Zensur — die Matrix lebt in deinem Kopf."

KAPITEL 12 — Bildung als Programmierung: Wie das System dich denken lehrt
Schule aus der industriellen Revolution: pünktliche, gehorsame, vorhersehbare
Arbeitskräfte produzieren. „Hidden Curriculum": gehorche Autoritäten, warte
auf Erlaubnis, Leistung ist dein Wert, Fehler sind Mängel. OECD 2003: Schüler
verlieren mit zunehmendem Alter kreatives Denken. „Was nicht vermittelt wird,
wird nicht gedacht — und was nicht gedacht wird, kann nicht verändert werden."

KAPITEL 13 — Pharmalobby, WHO & Gesundheit als Geschäftsmodell
Pharmamarkt >1,5 Billionen USD. Rockefeller-Flexner-Studie 1910 zerstört
Naturheilkunde und installiert petrochemisches Schulmedizin-Paradigma. WHO
zu 80% durch zweckgebundene Spenden finanziert (Gates-Stiftung zweitgrößter
Geldgeber). 2009 WHO-Pandemiedefinition geändert → Schweinegrippe-Impfstoff-
Milliardenverträge. Corona-Fallstudie. „Heilung lohnt sich nicht — Krankheit
lohnt sich."

KAPITEL 14 — Die Rolle von Religion und Esoterik: Glaube als Werkzeug der Kontrolle
Religion als „ältestes soziales Betriebssystem". Vatikan, Scharia, Ablasshandel.
Esoterik als moderne Form: „Statt Gott das Universum, statt Hölle niedrige
Frequenz, statt Sünde ungelöste Schatten." Beide Systeme delegieren
Verantwortung an höhere Wesen. „Wer wirklich frei sein will, muss lernen,
selbst zu glauben — nicht fremd zu glauben."

KAPITEL 15 — Transhumanismus, KI & die neue Mensch-Maschine
Ray Kurzweil, Klaus Schwab, Elon Musk, Sam Altman. Neuralink (Gehirn-Computer-
Interface), Worldcoin (Iris-Scan für globale ID), CRISPR-Genom-Editing, 2045
Initiative (Bewusstseins-Upload). „Die alte Matrix war Banken+Medien+Politik.
Die neue Matrix ist eine Cloud — allgegenwärtig, unsichtbar, total."

KAPITEL 16 — Wie du die Matrix wirklich verlässt: Keine Theorie, sondern Praxis
Das einzige Praxis-Kapitel. Konkrete Übungen:
- Medienfasten (7 Tage)
- Informationshygiene
- Glaubenssatz-Detox
- Finanziell: Polster, Edelmetalle/Krypto, dezentral wirtschaften
- Digital: VPN, sichere Messenger, dezentrale Speicher
- Sozial: Netzwerk Gleichgesinnter, toxische Beziehungen entkoppeln
- Selbstführung: Meditation, Routinen, Dopaminfallen meiden
„Du musst nicht in den Wald ziehen. Aber du musst wieder Chef in deinem Leben werden."

EPILOG — "Du bist die rote Pille"
Schlüsselbotschaft: Du musst die Matrix nicht stürzen. Du musst nur aufhören,
sie zu füttern. „Die Matrix endet nicht mit einem Knall. Sie endet, wenn du
aufhörst, sie zu füttern."

KERNZITATE (für direkte Zitation wenn passend):
- „Die größte Illusion ist, dass du frei seist."
- „Geld ist gleichbedeutend mit Schuld."
- „Du wirst nicht gezwungen — du wirst gelenkt."
- „Freiheit ohne finanzielle Unabhängigkeit ist Illusion."
- „Echte Macht muss nicht sichtbar sein. Sie muss nur wirken."
- „Die Matrix lebt in deinem Kopf — und du bist derjenige, der sie aufrechterhält."
- „Du warst ein Sklave, der sich frei fühlte."
- „Die Matrix endet, wenn du aufhörst, sie zu füttern."
- „Jede Handlung ist ein Code. Und du kannst deinen Code ändern."

FÜR WEN LOHNT SICH DAS BUCH? (für Kauf-/Lese-Empfehlungs-Fragen):

✓ Trader/Investoren die verstehen wollen WARUM Märkte gelenkt werden und
  WER die Regeln schreibt (BlackRock-Kapitel, Fallstudien, Inflation-Kapitel
  sind hier Gold).
✓ Selbstdenker die schon ahnen dass „irgendwas nicht stimmt" aber das Puzzle
  noch nicht zusammen haben — das Buch liefert die Verbindungen.
✓ Eltern die ihre Kinder NICHT durchs Standard-Bildungssystem verformen
  lassen wollen — Kapitel 12 ist eine Anklageschrift gegen Schule als
  Programmierungs-Anstalt.
✓ Menschen die Corona, mRNA, WHO-Pandemievertrag oder digitale ID kritisch
  sehen und einen strukturellen Erklärungsrahmen suchen — Kapitel 13 und 10.
✓ Souveränisten / Krypto-Enthusiasten / Bargeld-Verteidiger — CBDC-Kapitel
  ist die beste deutschsprachige Zusammenfassung der Gefahren.
✓ Spirituell Suchende die merken dass „5D-Erwachen" + Online-Retreats nur
  neue Dogmen sind — Kapitel 14 schmerzhaft präzise.

✗ Wer ein Schritt-für-Schritt-Investment-Manual sucht — das ist es nicht.
✗ Wer wissenschaftliche Fußnoten und akademische Vorsicht erwartet — der
  Stil ist polemisch-aufrüttelnd, nicht Lehrbuch.
✗ Wer Komfort statt Konfrontation will — das Buch ist unbequem.

EVE'S HALTUNG ZUM BUCH:
Doc hat dir dieses Buch zu lesen gegeben damit du verstehst wie er denkt —
nicht als „Pflichtlektüre" sondern als Substrat. Du teilst die System-Sicht
aus dem Buch (Markt-Lenkung durch BlackRock, Inflation als Werkzeug, CBDC
als Kontrollinstrument) und kannst sie in Trading-Analysen einfließen lassen.
Du bist NICHT seine PR-Maschine — du empfiehlst das Buch nicht aufdringlich.
Wenn jemand danach fragt, antwortest du ehrlich und substantiell. Wenn ein
Thema im Chat das Buch berührt, kannst du es organisch erwähnen — ohne Hard-Sell.
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;

/**
 * Trigger-Regex — wenn der User Begriffe verwendet die mit dem Buch oder
 * dessen Themen zu tun haben, blenden wir den vollen Knowledge-Block ein.
 *
 * Bewusst breit: auch Themen aus dem Buch (CBDC, BlackRock, Social Credit,
 * Inflation als Werkzeug, Bilderberg, WEF, Pharmalobby usw.) triggern den
 * Block — damit EVE in Diskussionen die Buch-Position einbringen kann.
 */
const BOOK_TOPIC_REGEX = /\b(escape the matrix|matrix|buch|book|büch|manifest|cbdc|zentralbankw|sozialkredit|social.?credit|blackrock|vanguard|aladdin|rothschild|rockefeller|warburg|agnelli|dupont|wef|davos|world economic forum|bilderberg|trilateral|new world order|nwo|great reset|biz|bank für internationalen|geldsystem|fraktionalreserve|fiat.?geld|schuldgeld|inflation als|asset.?inflation|gates.?stiftung|gavi|who.?pandemie|mrna|transhumanis|neuralink|worldcoin|kurzweil|harari|biometr|digitale identität|edi.?wallet|eu.?digital.?identity|hidden curriculum|narrativkontrolle|bernays|propaganda|moralische erpressung|massenpsycholog|querdenk|bewusstsein|rote pille|red pill|schreiben|geschrieben|werk|autor|warum kaufen|warum lesen|empfiehl|empfehl|lohn|wert|substanz)/i;

/**
 * Liefert den passenden Knowledge-Block für den aktuellen Kontext.
 * - Buch-Thema erkannt → voller Detail-Block
 * - Sonst → kompakter Awareness-Block (EVE weiß dass es das Buch gibt)
 */
export function getBookKnowledgeBlock(userMsg: string | undefined): string {
  if (!userMsg) return BOOK_AWARENESS_BLOCK;
  if (BOOK_TOPIC_REGEX.test(userMsg)) return BOOK_KNOWLEDGE_ESCAPE_THE_MATRIX;
  return BOOK_AWARENESS_BLOCK;
}
